import React, { useContext, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  createBrowserRouter, createRoutesFromElements, RouterProvider, Routes, Route, Navigate, Outlet
} from 'react-router-dom';
import { ToastContainer, Slide } from 'react-toastify';
import { ThemeProvider } from '@mui/material/styles';
import { lightTheme, darkTheme } from './mui/theme';
import {
  Modules, RouteParamsTypes, AuthRoutes, NonAuthRoutes,
} from './enums';
import { AuthContext } from './contexts/AuthContext';

// Pages
import Auth from './routes/Auth';
import Resources from './routes/Resources';
import My from './routes/My';
import Home from './routes/Home';
import Transactions from './routes/Transactions';
import Reservations from './routes/Reservations';
import Invoices from './routes/Invoices';
import Reconciliation from './routes/Reconciliation';
import Errors from './routes/Errors';

// Components
import HttpInterceptor from './components/HttpInterceptor'
import UnHandledRejection from './components/ErrorPages/UnHandledRejection'
import GlobalLoader from './components/GlobalLoader';
import ScrollToTop from './components/ScrollToTop';

// Styles
import './assets/vendor/fonts/remixicon/remixicon.css';
import './assets/vendor/fonts/flag-icons.css';
import './assets/vendor/libs/node-waves/node-waves.css';
import './assets/css/demo.css';
import './assets/vendor/libs/perfect-scrollbar/perfect-scrollbar.css';
import './assets/vendor/libs/typeahead-js/typeahead.css';
import './assets/vendor/libs/datatables-bs5/datatables.bootstrap5.css';
import './assets/vendor/libs/datatables-responsive-bs5/responsive.bootstrap5.css';
import './assets/vendor/libs/datatables-buttons-bs5/buttons.bootstrap5.css';
import './assets/vendor/libs/datatables-checkboxes-jquery/datatables.checkboxes.css';
import './assets/vendor/libs/select2/select2.css';
import './assets/vendor/libs/@form-validation/form-validation.css';
import './assets/vendor/css/pages/page-auth.css';
import './assets/vendor/css/pages/page-profile.css';
import './assets/app-only/css/style.scss';
import 'react-toastify/dist/ReactToastify.css';

// i18n
import './i18n/config';

const RootLayout: React.FC = () => {
  const { theme, loading, exception, setLoading } = useContext(AuthContext);
  const themeLinkRef = useRef<HTMLLinkElement | null>(null);
  const themeLinkRefTwo = useRef<HTMLLinkElement | null>(null);

  const loadCSS = (href: string): HTMLLinkElement => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.type = 'text/css';
    document.head.appendChild(link);
    return link;
  }

  useEffect(() => {
    const root = document.documentElement;
    if (themeLinkRef.current) {
      document.head.removeChild(themeLinkRef.current);
    }

    if (themeLinkRefTwo.current) {
      document.head.removeChild(themeLinkRefTwo.current);
    }

    const href = theme === 'light' ? '/theme/light/core.css' : '/theme/dark/core.css';
    const hrefTwo = theme === 'light' ? '/theme/light/theme-semi-dark.css' : '/theme/dark/theme-semi-dark.css';
    const hrefThree = '/theme/responsive.css';
    themeLinkRef.current = loadCSS(href);
    themeLinkRefTwo.current = loadCSS(hrefTwo);
    themeLinkRefTwo.current = loadCSS(hrefThree);
    root.classList.add(theme === 'light' ? 'light-style' : 'dark-style');
    root.classList.remove(theme === 'light' ? 'dark-style' : 'light-style');

    setTimeout(() => {
      setLoading('false')
    }, 500)
  }, [theme, setLoading]);

  return (
    <ThemeProvider theme={theme === 'light' ? lightTheme : darkTheme}>
      {exception && <UnHandledRejection />}
      {loading === 'true' && <GlobalLoader />}

      <HttpInterceptor />
      <ScrollToTop />
      <ToastContainer
        position="top-center"
        autoClose={5000}
        transition={Slide}
        newestOnTop
        closeOnClick
        rtl={false}
        draggable={false}
        closeButton={false}
        className="toast-container-custom"
      />

      {
        loading === 'false' && <Outlet />
      }
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  const { token } = useContext(AuthContext);

  const handleBlankRoute = useCallback(() => {
    if (!token) {
      return Modules.auth;
    } else {
      return Modules.home + AuthRoutes.dashboard;
    }
  }, [token]);

  const handleAnyRoute = useCallback(() => {
    if (!token) {
      return Modules.auth;
    }
    return Modules.error + NonAuthRoutes.notFound;
  }, [token]);

  const router = useMemo(() => createBrowserRouter(
    createRoutesFromElements(
      <Route element={<RootLayout />}>
        <Route path={AuthRoutes.blank} element={<Navigate replace to={{ pathname: handleBlankRoute() }} />} />
        <Route path={Modules.auth + RouteParamsTypes.any} element={<Auth />} />
        <Route path={Modules.resources + RouteParamsTypes.any} element={<Resources />} />
        <Route path={Modules.my + RouteParamsTypes.any} element={<My />} />
        <Route path={Modules.home + RouteParamsTypes.any} element={<Home />} />
        <Route path={Modules.transactions + RouteParamsTypes.any} element={<Transactions />} />
        <Route path={Modules.reservations + RouteParamsTypes.any} element={<Reservations />} />
        <Route path={Modules.invoices + RouteParamsTypes.any} element={<Invoices />} />
        <Route path={Modules.reconciliation + RouteParamsTypes.any} element={<Reconciliation />} />
        <Route path={Modules.error + RouteParamsTypes.any} element={<Errors />} />
        <Route path={RouteParamsTypes.any} element={<Navigate replace to={{ pathname: handleAnyRoute() }} />} />
      </Route>
    )
  ), [handleBlankRoute, handleAnyRoute]);

  return <RouterProvider router={router} />;
};

export default App;
